import React, { useEffect } from 'react'
import Image from 'next/image'
import { Primary, Text } from '@cinch-labs/design-system'
import styles from './booking-confirmation.module.css'
import ConfirmationTick from '../../assets/confirmation-tick.svg'
import { useUserStore } from '@cinch-nx/data-user'
import CinchServicingLogo from '../../assets/cinchServicing-logo.png'
import { datadogRum } from '@datadog/browser-rum'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { useAwin } from '../../hooks/use-awin'
import { getCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'

export const BookingConfirmation: React.FC = () => {
  const isLoggedIn = useUserStore((state) => state.isLoggedIn())
  const context = useMaintenanceContext()
  const {
    customerEmail,
    customerSurname,
    vehicleMake,
    vehicleModel,
    vrm,
    store,
    notes,
    step,
    serviceOptions,
    serviceOptionsSelectedNames,
    serviceOptionsSelectedTotalPrice,
    appointmentType,
    bookingDate,
    bookingTime,
    canBookAppointment,
  } = context

  const { sendAwinConfirmation } = useAwin()

  const bookingValueInPounds = Object.values(serviceOptions ?? {})
    .reduce((agg, cur) => (cur.selected ? agg + cur.price : agg), 0)
    .toFixed(2)

  useEffect(() => {
    datadogRum.addAction('Cinch Servicing: Booking Completed', {
      cinchServicing: true,
      email: customerEmail,
      step,
      page: window.location.pathname,
      serviceOptionsSelectedNames,
      serviceOptionsSelectedTotalPrice,
      bookingValueInPounds: Number.parseFloat(bookingValueInPounds),
      appointmentType,
      store,
      vehicleMake,
      vehicleModel,
      vrm,
      notes,
      bookingDate,
      bookingTime,
      canBookAppointment,
    })

    const { data } = getCustomerJourneyCookie()

    if (data && data.commissionGroup === 'SERVICING') {
      const bookingRef = `${vrm}:${customerSurname}`
      sendAwinConfirmation(bookingRef, bookingValueInPounds, data)
    }
  }, [])

  const serviceOptionText =
    serviceOptionsSelectedNames?.startsWith('Oil') ||
    serviceOptionsSelectedNames?.startsWith('MOT')
      ? `an ${serviceOptionsSelectedNames}`
      : `a ${serviceOptionsSelectedNames}`

  return (
    <div className={styles.container}>
      <div className={styles.successIcon}>
        <ConfirmationTick />
      </div>
      <Image
        src={CinchServicingLogo}
        alt="cinch servicing logo"
        width="271"
        height="64"
        className={styles.logo}
      />
      <Text
        element="h1"
        fontSizeFixed="3xl"
        fontWeight="bold"
        className={styles.title}
      >
        {canBookAppointment
          ? "We've got your booking!"
          : 'Thanks for your enquiry'}
      </Text>
      {canBookAppointment ? (
        <Text fontSizeFixed="m" className={styles.subtitle}>
          {`Your ${vehicleMake} ${vehicleModel}, ${vrm}, is booked for ${serviceOptionText} at ${store}`}
        </Text>
      ) : (
        <>
          <Text fontSizeFixed="m" className={styles.subtitle}>
            Sit tight! Someone from the Marshall team will call you to confirm
            your appointment soon.
          </Text>
          <Text fontSizeFixed="m" className={styles.subtitle}>
            You should hear back within 48 hours, between Monday and Friday
            (8am-6pm).
          </Text>
        </>
      )}
      {canBookAppointment && (
        <div className={styles.whatHappensNext}>
          <Text fontSizeFixed="xl" fontWeight="bold">
            What happens next
          </Text>
          <div className={styles.whatHappensBulletLine}>
            <div className={styles.bulletPoint}>
              <Text fontSizeFixed="xs" fontWeight="bold">
                1
              </Text>
            </div>
            <div className={styles.bulletTextContainer}>
              <Text
                fontSizeFixed="m"
                fontWeight="semibold"
                className={styles.bulletHeader}
              >
                Confirmation
              </Text>
              <Text fontSizeFixed="s">
                You'll get a booking confirmation email with all the info you
                need.
              </Text>
            </div>
          </div>
          <div className={styles.whatHappensBulletLine}>
            <div className={styles.bulletPoint}>
              <Text fontSizeFixed="xs" fontWeight="bold">
                2
              </Text>
            </div>
            <div className={styles.bulletTextContainer}>
              <Text
                fontSizeFixed="m"
                fontWeight="semibold"
                className={styles.bulletHeader}
              >
                On the day
              </Text>
              <Text fontSizeFixed="s">
                Bring your car at your chosen appointment time.
              </Text>
            </div>
          </div>
          <div className={styles.whatHappensBulletLine}>
            <div className={styles.bulletPoint}>
              <Text fontSizeFixed="xs" fontWeight="bold">
                3
              </Text>
            </div>
            <div className={styles.bulletTextContainer}>
              <Text
                fontSizeFixed="m"
                fontWeight="semibold"
                className={styles.bulletHeader}
              >
                Collection
              </Text>
              <Text fontSizeFixed="s">
                We'll let you know when your car's good to go - or discuss your
                options if it needs a bit more TLC.
              </Text>
            </div>
          </div>
          <div className={styles.whatHappensBulletLine}>
            <div className={styles.bulletPoint}>
              <Text fontSizeFixed="xs" fontWeight="bold">
                4
              </Text>
            </div>
            <div className={styles.bulletTextContainer}>
              <Text
                fontSizeFixed="m"
                fontWeight="semibold"
                className={styles.bulletHeader}
              >
                Payment
              </Text>
              <Text fontSizeFixed="s">
                Once the work's done, simply pay by card in-store. Job done.
              </Text>
            </div>
          </div>
        </div>
      )}

      {!isLoggedIn && (
        <Primary
          classNames={{ button: styles.viewButton }}
          aria-label={'Create a cinch account'}
          href="/sign-up?channel=cinch-servicing"
          element="a"
        >
          Create a cinch account
        </Primary>
      )}
    </div>
  )
}
